import React, { useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../../Atoms/Modal/Modal';
import IconPicker, { iconPreviewSrc } from './IconPicker';
import SearchableSelect from './SearchableSelect';
import styles from './MenuItemEditor.module.scss';
import {
  useCreateNavigationItem,
  useCreatePlacement,
  useUpdateNavigationItem,
  useUpdatePlacement,
  useItemPlacements,
  usePages,
  useDegreeRoutes,
  buildDegreeRouteGroups,
  PAGE_TYPE,
  PLACEMENT_TYPE,
} from '../../../Hooks/NavigationMenu';
import {
  DESTINATION,
  menuActionOptions,
  PATH_SOURCE,
  destinationOf,
  pathSourceOf,
  toApiPayloads,
  validateMenuForm,
} from '../../../ValidationSchema/navigationMenu';

/**
 * Create or edit one menu entry — the item and its placement together.
 *
 * The API models these as two rows, and it is right to: one item can appear in
 * several menus. But an operator adding "تواصل معنا" to a menu is doing ONE
 * thing, so the form presents one thing and writes both rows.
 *
 * The same separation is what makes «نسخ إلى قوائم أخرى» possible: a copy is a
 * second PLACEMENT for the same item, never a second item. Copying «الإحصائيات»
 * into the featured row and later renaming it renames it in both places, which
 * is the behaviour an operator expects, and the reason this is not a clone.
 *
 * Reference: DPA-FE `docs/Controlled/MainMenu/04-cms-workplan.md` §3d.
 */

const DESTINATION_OPTIONS = [
  {
    key: DESTINATION.PAGE,
    label: 'صفحة من الموقع',
    hint: 'اختيار من صفحات الموقع المسجّلة',
  },
  {
    key: DESTINATION.PAGE_SECTION,
    label: 'قسم من الصفحة الرئيسية',
    hint: 'اختيار من أقسام الصفحات المسجّلة، مثل ‎/#news',
  },
  {
    key: DESTINATION.DYNAMIC,
    label: 'صفحة ديناميكية',
    hint: 'رابط يُبنى ديناميكياً — مثل صفحة برنامج أكاديمي',
  },
  { key: DESTINATION.ACTION, label: 'إجراء', hint: 'بحث، لغة، تسجيل دخول…' },
  // «تمرير إلى قسم مرقّم» — WITHDRAWN 2026-09-29, not deleted. The fixed,
  // numbered scroll the bottom bar uses (`#section-n`, resolved by the website
  // from an index), as distinct from «قسم من الصفحة الرئيسية» above, which is
  // an authored catalogue row. Commented out here so no NEW entry can choose
  // it; every other branch (validation, payload, read-back, the select below)
  // is left intact, so the `#section-n` rows already in the database still
  // open, validate and save. Restore this block to bring the option back.
  // {
  //   key: DESTINATION.SECTION,
  //   label: 'تمرير إلى قسم مرقّم',
  //   hint: 'الشريط السفلي فقط — ‎#section-1‎ وما بعده',
  // },
  { key: DESTINATION.EXTERNAL, label: 'رابط خارجي', hint: 'موقع خارج نطاق الأكاديمية' },
];

/**
 * =============================================================================
 * THE DIALOG IS A WORKFLOW, NOT A WALL OF FIELDS
 * =============================================================================
 * This form asks for four unrelated kinds of thing — what the entry is called,
 * where it goes, how it looks, and which other menus should carry it. Stacked
 * as four open fieldsets they read as one long scroll in which «حفظ» could sit
 * below three sections the operator never needed, and an error in the first
 * section could be off-screen when the button was pressed.
 *
 * One step at a time, with the trail visible above, makes the shape of the
 * task legible before it is started and keeps every error next to the control
 * that caused it.
 *
 * The steps are NOT re-validated as gates in isolation — `validateMenuForm`
 * already validates the whole form on every keystroke, and `ERROR_STEP` below
 * is only the map from an error back to the step that owns it, so «التالي» can
 * refuse to advance and «حفظ» can jump back to the first step that is wrong.
 */
const STEP = Object.freeze({
  ITEM: 'item',
  DESTINATION: 'destination',
  DISPLAY: 'display',
  COPY: 'copy',
});

/** Which step owns each key `validateMenuForm` can return. */
const ERROR_STEP = Object.freeze({
  title: STEP.ITEM,
  destination: STEP.DESTINATION,
  internalPageId: STEP.DESTINATION,
  linkOverride: STEP.DESTINATION,
  externalUrl: STEP.DESTINATION,
  isShowButtonText: STEP.DISPLAY,
  isHeadTitle: STEP.DISPLAY,
  iconUrl: STEP.DISPLAY,
});

const emptyForm = {
  title: '',
  description: '',
  destination: DESTINATION.PAGE,
  internalPageId: '',
  linkOverride: '',
  // UI-only: which control fills `linkOverride` for a PATH destination.
  // Derived on open, never sent — see `PATH_SOURCE`.
  pathSource: PATH_SOURCE.MANUAL,
  externalUrl: '',
  iconUrl: '',
  openInNewTab: false,
  isActive: true,
  isVisible: true,
  isDisplayTypeButton: false,
  isShowButtonText: false,
  isHeadTitle: false,
};

/** First of several candidates that is actually a non-empty string. */
const firstFilled = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value;
  }
  return '';
};

const formFromPlacement = (placement) => {
  if (!placement) return emptyForm;
  const item = placement.item ?? {};
  const destination = destinationOf(placement);

  // One control (`linkOverride`) is filled from whichever field the row
  // actually carries: the placement's own override, a dynamic page's URL, or
  // a procedure's `procedureName`. `??` alone is not enough — the API returns
  // `''` as readily as `null`, and an empty override must not win over a
  // populated `procedureName`.
  const link = firstFilled(
    placement.linkOverride,
    item.dynamicUrl,
    item.procedureName
  );

  return {
    title: item.title ?? '',
    description: item.description ?? '',
    destination,
    internalPageId: item.page?.id ?? '',
    linkOverride: link,
    pathSource: pathSourceOf(link),
    externalUrl: item.externalUrl ?? '',
    iconUrl: item.iconUrl ?? '',
    openInNewTab: Boolean(item.openInNewTab),
    isActive: item.isActive !== false,
    isVisible: placement.isVisible !== false,
    isDisplayTypeButton: Boolean(item.isDisplayTypeButton),
    isShowButtonText: Boolean(item.isShowButtonText),
    isHeadTitle: Boolean(item.isHeadTitle),
  };
};

const MenuItemEditor = ({
  placement, // existing placement to edit, or null to create
  placementType,
  placementConfig,
  parentPlacementId = null,
  nextSequenceId = 0,
  onClose,
}) => {
  const isEdit = Boolean(placement?.id);
  const [form, setForm] = useState(() => formFromPlacement(placement));
  const [touched, setTouched] = useState(false);
  // Placement types to copy this entry into on save. Holds the operator's
  // INTENT only; whether a type is still copyable is re-decided from
  // `duplicateOptions` at save time, so a menu that filled up or received the
  // item elsewhere while this dialog was open cannot be written twice.
  const [duplicateTargets, setDuplicateTargets] = useState([]);

  const [isIconPickerOpen, setIconPickerOpen] = useState(false);
  // The one scrolling region, so a step change can return it to the top.
  const bodyRef = useRef(null);

  // Two reads of ONE catalogue, split by `PageType`: routes the website
  // serves, and regions inside them. Separate query keys, so switching
  // destination does not refetch the other list.
  const { data: pagesPage, isLoading: isPagesLoading } = usePages(PAGE_TYPE.PAGE);
  const { data: sectionsPage, isLoading: isSectionsLoading } = usePages(
    PAGE_TYPE.SECTION
  );
  // Memoised from `pagesPage` rather than from a `?? []` expression: a fresh
  // array literal on every render would re-run every dependent useMemo.
  const pages = useMemo(() => pagesPage?.items ?? [], [pagesPage]);
  const sections = useMemo(() => sectionsPage?.items ?? [], [sectionsPage]);
  const knownRoutes = useMemo(
    () => pages.map((page) => page.slug).filter(Boolean),
    [pages]
  );

  // The live programme list, so «البرامج» offers real degrees instead of
  // asking an operator to paste a GUID.
  const { data: degreeData, isLoading: isDegreesLoading } = useDegreeRoutes();
  const degreeGroups = useMemo(
    () => buildDegreeRouteGroups(degreeData),
    [degreeData]
  );

  // Every placement type, so the copy list can say which menus already hold
  // this item. Shares `usePlacements`'s query key, so the type behind this
  // dialog is served from cache rather than fetched a second time.
  const { byType, isLoading: isPlacementMapLoading } = useItemPlacements(
    placement?.navigationItemId ?? null
  );

  const createItem = useCreateNavigationItem();
  const updateItem = useUpdateNavigationItem();
  const createPlacement = useCreatePlacement();
  const updatePlacement = useUpdatePlacement();

  const isSaving =
    createItem.isLoading ||
    updateItem.isLoading ||
    createPlacement.isLoading ||
    updatePlacement.isLoading;

  const { errors, warnings } = useMemo(
    () => validateMenuForm(form, { knownRoutes }),
    [form, knownRoutes]
  );
  const hasErrors = Object.keys(errors).length > 0;

  /**
   * «نسخ إلى قوائم أخرى» BELONGS TO THE MAIN MENU ONLY.
   *
   * The control was offered on all five placements, which reads as an
   * invitation to seed the action bar, the featured row and the bottom bar
   * from wherever the operator happens to be standing. Those four are small,
   * fixed, position-sensitive regions — the action bar is capped at five by
   * RSP-03 — whereas the main menu is the full catalogue and the only place
   * where "this entry also belongs over there" is a routine thought.
   *
   * Copying is not lost for the others: an entry is still added to them from
   * their own screen. What is removed is the sideways path INTO them.
   */
  /* ----------------------------------------------- searchable select data --
   * One shape for every list: `{ value, label, hint?, group? }`.
   *
   * The PATH goes in `hint`, not glued to the label with an em dash as the
   * old `<option>` text did. A native option cannot hold two lines, so title
   * and slug had to share one; the new control draws the slug underneath in a
   * quieter weight and LTR — which is what a path is — and still MATCHES on
   * it, so «/admissions» finds the admissions page by path as well as name.
   */
  const pageOptions = useMemo(
    () =>
      pages.map((page) => ({
        value: page.id,
        label: page.title,
        hint: page.slug,
      })),
    [pages]
  );

  const sectionOptions = useMemo(
    () =>
      sections.map((section) => ({
        // The SLUG is the value here, not the id — see the note on
        // `usesOverride` in the validation schema.
        value: section.slug,
        label: section.title,
        hint: section.slug,
      })),
    [sections]
  );

  // Flattened from `<optgroup>`s: the control repeats a group heading only
  // when it changes, which reproduces the grouping without nesting.
  const degreeOptions = useMemo(
    () =>
      degreeGroups.flatMap((group) =>
        group.options.map((option) => ({
          value: option.route,
          label: option.label,
          group: group.label,
        }))
      ),
    [degreeGroups]
  );

  const actionOptions = useMemo(
    () =>
      menuActionOptions(form.linkOverride).map((action) => ({
        value: action.key,
        label: action.label,
        hint: action.retired ? 'لم يعد متاحاً للاختيار' : undefined,
      })),
    [form.linkOverride]
  );

  const isMainMenu = Number(placementType) === PLACEMENT_TYPE.MAIN_MENU;

  /**
   * The catalogue row id behind the chosen «قسم من الصفحة الرئيسية».
   *
   * The select stores the section's SLUG, because that is what the website
   * resolves a link from. The API additionally needs the row id: a page
   * section now sends `linkType: 2`, and the server rejects every linkType but
   * External and Dynamic when `internalPageId` is null.
   *
   * Resolved from the catalogue rather than stored in the form on purpose —
   * rows saved BEFORE this change carry a slug and no id, and looking it up
   * here backfills them on the next save with no migration and no second piece
   * of form state to keep in step.
   */
  const pageSectionId = useMemo(() => {
    if (form.destination !== DESTINATION.PAGE_SECTION) return null;
    const slug = (form.linkOverride ?? '').trim();
    if (!slug) return null;
    return sections.find((section) => section.slug === slug)?.id ?? null;
  }, [form.destination, form.linkOverride, sections]);

  /* --------------------------------------------------------------- steps -- */

  const steps = useMemo(() => {
    const list = [
      { id: STEP.ITEM, label: 'العنصر', hint: 'العنوان والوصف' },
      { id: STEP.DESTINATION, label: 'الوجهة', hint: 'إلى أين ينتقل الزائر' },
      { id: STEP.DISPLAY, label: 'العرض', hint: 'الأيقونة وخيارات الظهور' },
    ];
    if (isMainMenu) {
      list.push({
        id: STEP.COPY,
        label: 'نسخ إلى قوائم أخرى',
        hint: 'اختياري',
      });
    }
    return list;
  }, [isMainMenu]);

  const [stepId, setStepId] = useState(STEP.ITEM);
  // The step list shrinks when the dialog is opened on a non-main menu, so a
  // remembered COPY step could point at a step that no longer exists.
  const stepIndex = Math.max(
    0,
    steps.findIndex((step) => step.id === stepId)
  );
  const currentStep = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;

  /** Every step that currently holds at least one validation error. */
  const erroredSteps = useMemo(() => {
    const owners = new Set();
    for (const key of Object.keys(errors)) {
      owners.add(ERROR_STEP[key] ?? STEP.ITEM);
    }
    return owners;
  }, [errors]);

  const goToStep = (id) => {
    setStepId(id);
    // The body scrolls; a step change that left the reader half-way down the
    // previous one would look like nothing happened.
    bodyRef.current?.scrollTo({ top: 0 });
  };

  const handleNext = () => {
    // Errors for the step being left must become visible at the moment the
    // operator tries to leave it — not silently later, at save.
    setTouched(true);
    if (erroredSteps.has(currentStep.id)) return;
    goToStep(steps[Math.min(stepIndex + 1, steps.length - 1)].id);
  };

  const handleBack = () => goToStep(steps[Math.max(stepIndex - 1, 0)].id);

  /**
   * The other menus, each carrying WHY it can or cannot take a copy.
   *
   * «Already there» is shown, not hidden: an operator asking «why can I not add
   * this to the featured row» needs the answer on the row itself, and a menu
   * that silently vanished from the list would read as a bug. The other blocks
   * reuse rules this screen already enforces rather than inventing new ones —
   * the action bar's item cap (RSP-03), and the rule that a section scroll is
   * only meaningful on a menu that supports buttons.
   */
  const duplicateOptions = useMemo(
    () =>
      byType
        .filter((entry) => entry.type !== Number(placementType))
        .map((entry) => {
          if (entry.isError) {
            return {
              ...entry,
              presence: 'blocked',
              reason: 'تعذّر قراءة هذه القائمة، فلا يمكن التأكد من عدم التكرار.',
            };
          }
          if (entry.hasItem) {
            return {
              ...entry,
              presence: 'present',
              reason: 'مضاف إلى هذه القائمة بالفعل.',
            };
          }
          if (entry.config.maxItems && entry.rootCount >= entry.config.maxItems) {
            return {
              ...entry,
              presence: 'blocked',
              reason: `القائمة مكتملة — الحد الأقصى ${entry.config.maxItems} عناصر.`,
            };
          }
          if (
            form.destination === DESTINATION.SECTION &&
            !entry.config.supportsButtons
          ) {
            return {
              ...entry,
              presence: 'blocked',
              reason: 'التمرير إلى قسم لا يعمل في هذه القائمة.',
            };
          }
          return {
            ...entry,
            presence: 'available',
            reason: `${entry.rootCount} عنصر حالياً في هذه القائمة.`,
          };
        }),
    [byType, placementType, form.destination]
  );

  // Derived, never stored: a target that stopped being available while the
  // dialog was open drops out here instead of being written.
  const selectedTargets = useMemo(
    () =>
      isMainMenu
        ? duplicateOptions.filter(
            (option) =>
              option.presence === 'available' &&
              duplicateTargets.includes(option.type)
          )
        : // The control is not rendered off the main menu, so this is belt and
          // braces: nothing can reach `handleSave`'s copy loop from a screen
          // that never offered the choice.
          [],
    [duplicateOptions, duplicateTargets, isMainMenu]
  );

  const toggleDuplicateTarget = (type) =>
    setDuplicateTargets((previous) =>
      previous.includes(type)
        ? previous.filter((value) => value !== type)
        : [...previous, type]
    );

  const set = (patch) => setForm((previous) => ({ ...previous, ...patch }));

  const handleDestinationChange = (destination) => {
    // Clear the fields that belong to the destination being left, so a stale
    // externalUrl cannot travel with an internal item.
    set({
      destination,
      internalPageId: '',
      linkOverride: '',
      externalUrl: '',
      pathSource: PATH_SOURCE.MANUAL,
      // An external link almost always wants a new tab; an internal one never
      // does. Setting the sensible default here saves a second decision.
      openInNewTab: destination === DESTINATION.EXTERNAL,
    });
  };

  /**
   * Write the copies — one extra PLACEMENT per selected menu, same item id.
   *
   * Sequential and individually caught, because each copy is an independent
   * write and a failure on the third must not discard the first two. The
   * outcome is reported BY MENU NAME: a bare «تعذّر النسخ» would leave an
   * operator unable to tell which menus actually received the entry.
   */
  const copyIntoTargets = async (navigationItemId, targets) => {
    const copied = [];
    const failed = [];

    for (const target of targets) {
      const { placement: copyPayload } = toApiPayloads(form, {
        placementType: target.type,
        pageSectionId,
        // A copy always lands at the TOP level of its destination: a
        // `parentPlacementId` from THIS menu names a row that does not exist
        // in the other one. `buildPlacementTree` would promote such a row to
        // the root anyway — better to say so in the UI than to write a
        // dangling parent and let the tree quietly repair it.
        parentPlacementId: null,
        sequenceId: target.nextSequenceId,
      });

      try {
        // eslint-disable-next-line no-await-in-loop
        await createPlacement.mutateAsync({ navigationItemId, ...copyPayload });
        copied.push(target.config.label);
      } catch (error) {
        failed.push(`${target.config.label} (${error?.message || 'خطأ'})`);
      }
    }

    return { copied, failed };
  };

  const handleSave = async () => {
    setTouched(true);
    if (isSaving) return;
    if (hasErrors) {
      // «حفظ» used to simply do nothing when a field three steps back was
      // wrong. Land the operator on the error instead of describing it.
      const broken = steps.find((step) => erroredSteps.has(step.id));
      if (broken) goToStep(broken.id);
      return;
    }

    const targets = selectedTargets;

    const { item, placement: placementPayload } = toApiPayloads(form, {
      placementType,
      parentPlacementId: isEdit ? placement.parentPlacementId : parentPlacementId,
      sequenceId: isEdit ? placement.sequenceId : nextSequenceId,
      pageSectionId,
    });

    let itemId = placement?.navigationItemId ?? null;

    try {
      if (isEdit) {
        await updateItem.mutateAsync({ id: placement.navigationItemId, ...item });
        await updatePlacement.mutateAsync({
          id: placement.id,
          navigationItemId: placement.navigationItemId,
          ...placementPayload,
        });
      } else {
        const created = await createItem.mutateAsync(item);
        itemId = created?.id ?? created?.navigationItemId;
        if (!itemId) {
          // Contract §5.3 — a create returns the new id. Without it the
          // placement cannot be written, and a half-created entry is worse
          // than a failed one.
          throw new Error('لم يُرجع الخادم معرّف العنصر الجديد.');
        }
        await createPlacement.mutateAsync({
          navigationItemId: itemId,
          ...placementPayload,
        });
      }
    } catch (error) {
      // The entry itself failed, so nothing is copied — the dialog stays open
      // with the operator's input intact.
      toast.error(error?.message || 'تعذّر حفظ العنصر.');
      return;
    }

    const saved = isEdit ? 'تم حفظ التعديلات.' : 'تمت إضافة العنصر.';

    if (targets.length === 0) {
      toast.success(saved);
      onClose?.();
      return;
    }

    const { copied, failed } = await copyIntoTargets(itemId, targets);

    toast.success(
      copied.length > 0
        ? `${saved} وأُضيف أيضاً إلى: ${copied.join('، ')}.`
        : saved
    );

    if (failed.length > 0) {
      // The dialog still closes: the entry IS saved, and re-opening it shows
      // the menus that succeeded as «مضاف بالفعل» and the rest as available —
      // so a retry cannot double-write the ones that worked.
      toast.error(`تعذّرت الإضافة إلى: ${failed.join('، ')}.`);
    }

    onClose?.();
  };

  const showError = (field) => (touched ? errors[field] : null);

  return (
    /*
     * The shared Modal atom supplies ONLY the portal, the backdrop and Escape.
     * Its own chrome is suppressed the same way MessageDetailsModal does it,
     * and for the same measured reasons:
     *
     *   - its `saveButton` renders at `position: absolute; top: 35px;
     *     right: 100px` (Modal.module.scss), which lands EXACTLY on top of an
     *     RTL title — the reported "حفظ over the heading" defect;
     *   - its close control is an <img onClick>: not focusable, not
     *     keyboard-operable, and hidden here via the `closeStyle` inline prop
     *     because its CSS-module class name is hashed and unreachable.
     *
     * The dialog below owns its header (title + real close button), ONE
     * scrolling body, and a footer whose actions are always on screen — so
     * حفظ can never be scrolled away or overlap anything again.
     */
    <Modal
      onClose={onClose}
      closeWhite={false}
      innerContainerClass={styles['editor-modal']}
      modalInnerContainerStyle={{ padding: 0, gap: 0 }}
      closeStyle={{ display: 'none' }}
      closeOnBackdrop
    >
      <div
        className={styles.dialog}
        dir='rtl'
        role='dialog'
        aria-modal='true'
        aria-labelledby='menu-item-editor-title'
      >
        <header className={styles['dialog-head']}>
          <div className={styles['dialog-head-text']}>
            <h2 id='menu-item-editor-title' className={styles['dialog-title']}>
              {isEdit ? 'تعديل عنصر القائمة' : 'إضافة عنصر إلى القائمة'}
            </h2>
            {placementConfig?.label && (
              <span className={styles['dialog-context']}>
                {placementConfig.label}
              </span>
            )}
          </div>
          <button
            type='button'
            className={styles['dialog-close']}
            onClick={onClose}
            aria-label='إغلاق'
          >
            <span aria-hidden='true'>&times;</span>
          </button>
        </header>

        {/* The trail. A completed step stays clickable so a correction never
            needs the whole sequence walked again; a step that is still ahead
            is not, because its fields depend on choices not yet made (the
            destination step decides which controls the display step shows).
            `aria-current` is what a screen reader reads as "you are here". */}
        <nav className={styles.steps} aria-label='خطوات تحرير العنصر'>
          <ol className={styles['steps-list']}>
            {steps.map((step, index) => {
              const isCurrent = step.id === currentStep.id;
              const isDone = index < stepIndex;
              const hasError = touched && erroredSteps.has(step.id);

              return (
                <li key={step.id} className={styles['steps-item']}>
                  <button
                    type='button'
                    className={[
                      styles.step,
                      isCurrent ? styles['step-current'] : '',
                      isDone ? styles['step-done'] : '',
                      hasError ? styles['step-error'] : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => goToStep(step.id)}
                    disabled={!isDone && !isCurrent}
                    aria-current={isCurrent ? 'step' : undefined}
                  >
                    <span className={styles['step-marker']} aria-hidden='true'>
                      {hasError ? '!' : isDone ? '✓' : index + 1}
                    </span>
                    <span className={styles['step-text']}>
                      <strong>{step.label}</strong>
                      <small>{step.hint}</small>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <div className={styles['dialog-body']} ref={bodyRef}>
          <div className={styles.form}>
        {/* ---------------------------------------------------------- item */}
        <fieldset
          className={styles.fieldset}
          hidden={currentStep.id !== STEP.ITEM}
        >
          <legend className={styles.legend}>العنصر</legend>

          <label className={styles.field}>
            <span className={styles.label}>
              العنوان <em>مطلوب</em>
            </span>
            <input
              type='text'
              className={`${styles.input} ${showError('title') ? styles['input-error'] : ''}`}
              value={form.title}
              maxLength={100}
              onChange={(event) => set({ title: event.target.value })}
              placeholder='مثال: نشأة الأكاديمية'
            />
            {showError('title') && <span className={styles.error}>{errors.title}</span>}
            {!showError('title') && warnings.title && (
              <span className={styles.warning}>{warnings.title}</span>
            )}
          </label>

          <label className={styles.field}>
            <span className={styles.label}>الوصف</span>
            <textarea
              className={styles.textarea}
              rows={2}
              value={form.description}
              onChange={(event) => set({ description: event.target.value })}
              placeholder='يُستخدم كنص بديل للأزرار التي تظهر بأيقونة فقط'
            />
          </label>
        </fieldset>

        {/* --------------------------------------------------- destination */}
        <fieldset
          className={styles.fieldset}
          hidden={currentStep.id !== STEP.DESTINATION}
        >
          <legend className={styles.legend}>الوجهة</legend>

          <div className={styles['destination-grid']}>
            {DESTINATION_OPTIONS.filter(
              (option) =>
                // Section scrolls only make sense on the home page's own bar.
                option.key !== DESTINATION.SECTION || placementConfig?.supportsButtons
            ).map((option) => (
              <label
                key={option.key}
                className={`${styles['destination-option']} ${
                  form.destination === option.key ? styles['destination-active'] : ''
                }`}
              >
                <input
                  type='radio'
                  name='destination'
                  checked={form.destination === option.key}
                  onChange={() => handleDestinationChange(option.key)}
                />
                <span>
                  <strong>{option.label}</strong>
                  <small>{option.hint}</small>
                </span>
              </label>
            ))}
          </div>

          {/* Each searchable field is a <div>, not a <label>: SearchableSelect
              is a button plus a filter input, and a wrapping label would put
              both inside the field's accessible name and would focus the
              trigger when the filter is clicked. `aria-labelledby` names them
              instead. */}
          {form.destination === DESTINATION.PAGE && (
            <div className={styles.field}>
              <span className={styles.label} id='menu-page-label'>
                الصفحة
              </span>
              <SearchableSelect
                ariaLabelledBy='menu-page-label'
                value={form.internalPageId}
                onChange={(internalPageId) => set({ internalPageId })}
                options={pageOptions}
                disabled={isPagesLoading}
                invalid={Boolean(showError('internalPageId'))}
                placeholder={isPagesLoading ? 'جارٍ التحميل…' : 'اختر صفحة'}
                searchPlaceholder='ابحث بالاسم أو المسار…'
              />
              {showError('internalPageId') && (
                <span className={styles.error}>{errors.internalPageId}</span>
              )}
            </div>
          )}

          {/* Sections come from the SAME catalogue as pages, filtered by
              PageType=Section. Stored as the slug rather than the row id —
              see the note on `usesOverride` in the validation schema. */}
          {form.destination === DESTINATION.PAGE_SECTION && (
            <div className={styles.field}>
              <span className={styles.label} id='menu-section-label'>
                القسم
              </span>
              <SearchableSelect
                ariaLabelledBy='menu-section-label'
                value={form.linkOverride}
                onChange={(linkOverride) => set({ linkOverride })}
                options={sectionOptions}
                disabled={isSectionsLoading}
                invalid={Boolean(showError('linkOverride'))}
                placeholder={isSectionsLoading ? 'جارٍ التحميل…' : 'اختر قسماً'}
                searchPlaceholder='ابحث بالاسم أو المسار…'
              />
              {showError('linkOverride') && (
                <span className={styles.error}>{errors.linkOverride}</span>
              )}
              {!isSectionsLoading && sections.length === 0 && (
                <span className={styles.warning}>
                  لا توجد أقسام مسجّلة بعد. تُقرأ من
                  <code> GET /api/Pages/0/0/1</code> — أضف الأقسام إلى
                  فهرس الصفحات أولاً.
                </span>
              )}
              {/* A row saved before this screen sent an id, whose section has
                  since been renamed or removed from the catalogue: the slug
                  survives but resolves to nothing, so the save would be
                  refused for a missing `internalPageId`. Said here, on the
                  control, rather than as a server error after حفظ. */}
              {!isSectionsLoading &&
                sections.length > 0 &&
                form.linkOverride &&
                !pageSectionId && (
                  <span className={styles.warning}>
                    هذا القسم لم يعد موجوداً في فهرس الصفحات. اختر قسماً من
                    القائمة قبل الحفظ.
                  </span>
                )}
            </div>
          )}

          {/* «صفحة ديناميكية» — sent as `linkType: 3` + `dynamicUrl`. The URL
              has two ways in: «كتابة يدوية» is free text, still needed
              because the Pages catalogue covers 11 of ~37 routes;
              «البرامج الأكاديمية» resolves a live programme to its real URL,
              which is otherwise a GUID an operator has to copy out of
              another screen. Both write the same field. */}
          {form.destination === DESTINATION.DYNAMIC && (
            <>
              <label className={styles.field}>
                <span className={styles.label}>مصدر الرابط</span>
                <select
                  className={styles.input}
                  value={form.pathSource}
                  onChange={(event) =>
                    // Clearing the link on switch is deliberate: a programme
                    // URL left behind in the manual box reads as a typed path
                    // and would survive a save the operator did not intend.
                    set({ pathSource: event.target.value, linkOverride: '' })
                  }
                >
                  <option value={PATH_SOURCE.MANUAL}>كتابة يدوية</option>
                  <option value={PATH_SOURCE.PROGRAMS}>البرامج الأكاديمية</option>
                </select>
              </label>

              {form.pathSource === PATH_SOURCE.PROGRAMS ? (
                <div className={styles.field}>
                  <span className={styles.label} id='menu-degree-label'>
                    البرنامج
                  </span>
                  <SearchableSelect
                    ariaLabelledBy='menu-degree-label'
                    value={form.linkOverride}
                    onChange={(linkOverride) => set({ linkOverride })}
                    options={degreeOptions}
                    disabled={isDegreesLoading}
                    invalid={Boolean(showError('linkOverride'))}
                    placeholder={
                      isDegreesLoading ? 'جارٍ التحميل…' : 'اختر برنامجاً'
                    }
                    searchPlaceholder='ابحث باسم البرنامج…'
                  />
                  {showError('linkOverride') && (
                    <span className={styles.error}>{errors.linkOverride}</span>
                  )}
                  {/* The resolved URL is shown, not hidden: it is what gets
                      stored, and an operator checking a link against the live
                      site needs to be able to read it. */}
                  {form.linkOverride && (
                    <code className={`${styles.hint} ${styles.ltr}`}>
                      {form.linkOverride}
                    </code>
                  )}
                  {!isDegreesLoading && degreeGroups.length === 0 && (
                    <span className={styles.warning}>
                      تعذّر تحميل قائمة البرامج. يمكن اختيار «كتابة يدوية»
                      وإدخال المسار مباشرة.
                    </span>
                  )}
                </div>
              ) : (
                <label className={styles.field}>
                  <span className={styles.label}>الرابط الديناميكي</span>
                  <input
                    type='text'
                    dir='ltr'
                    className={`${styles.input} ${styles.ltr} ${
                      showError('linkOverride') ? styles['input-error'] : ''
                    }`}
                    value={form.linkOverride}
                    onChange={(event) =>
                      set({ linkOverride: event.target.value })
                    }
                    placeholder='/about/team'
                  />
                  {showError('linkOverride') && (
                    <span className={styles.error}>{errors.linkOverride}</span>
                  )}
                  {!showError('linkOverride') && warnings.linkOverride && (
                    <span className={styles.warning}>{warnings.linkOverride}</span>
                  )}
                </label>
              )}
            </>
          )}

          {form.destination === DESTINATION.ACTION && (
            <div className={styles.field}>
              <span className={styles.label} id='menu-action-label'>
                الإجراء
              </span>
              <SearchableSelect
                ariaLabelledBy='menu-action-label'
                value={form.linkOverride}
                onChange={(linkOverride) => set({ linkOverride })}
                options={actionOptions}
                invalid={Boolean(showError('linkOverride'))}
                placeholder='اختر إجراءً'
              />
              {showError('linkOverride') && (
                <span className={styles.error}>{errors.linkOverride}</span>
              )}
              <span className={styles.hint}>
                عناوين البوابة لا تُكتب هنا — الموقع يعرف عنوان كل بوابة، وهذا
                الخيار يحدد الوجهة فقط.
              </span>
            </div>
          )}

          {/* Reachable only for a row ALREADY saved as `#section-n` — the
              radio that creates one is commented out above. */}
          {form.destination === DESTINATION.SECTION && (
            <label className={styles.field}>
              <span className={styles.label}>القسم</span>
              <select
                className={`${styles.input} ${
                  showError('linkOverride') ? styles['input-error'] : ''
                }`}
                value={form.linkOverride}
                onChange={(event) => set({ linkOverride: event.target.value })}
              >
                <option value=''>اختر قسماً</option>
                <option value='#section-1'>القسم 1 — المركز الإعلامي</option>
                <option value='#section-2'>القسم 2 — البرامج</option>
                <option value='#section-3'>القسم 3 — حياة الطالب</option>
              </select>
              {showError('linkOverride') && (
                <span className={styles.error}>{errors.linkOverride}</span>
              )}
            </label>
          )}

          {form.destination === DESTINATION.EXTERNAL && (
            <label className={styles.field}>
              <span className={styles.label}>الرابط</span>
              <input
                type='url'
                dir='ltr'
                className={`${styles.input} ${styles.ltr} ${
                  showError('externalUrl') ? styles['input-error'] : ''
                }`}
                value={form.externalUrl}
                onChange={(event) => set({ externalUrl: event.target.value })}
                placeholder='https://www.example.com/'
              />
              {showError('externalUrl') && (
                <span className={styles.error}>{errors.externalUrl}</span>
              )}
            </label>
          )}
        </fieldset>

        {/* -------------------------------------------------------- display */}
        <fieldset
          className={styles.fieldset}
          hidden={currentStep.id !== STEP.DISPLAY}
        >
          <legend className={styles.legend}>العرض</legend>

          {/* A <div>, not a <label>: a label wrapping the browse button would
              make clicking it also focus the text input, and screen readers
              would announce the button as part of the field's name. */}
          <div className={styles.field}>
            <span className={styles.label} id='menu-icon-label'>
              الأيقونة
            </span>
            <div className={styles['icon-row']}>
              <span className={styles['icon-preview']} aria-hidden='true'>
                {form.iconUrl ? (
                  <img src={iconPreviewSrc(form.iconUrl)} alt='' decoding='async' />
                ) : (
                  <span className={styles['icon-empty']}>—</span>
                )}
              </span>
              <input
                type='text'
                dir='ltr'
                aria-labelledby='menu-icon-label'
                className={`${styles.input} ${styles.ltr}`}
                value={form.iconUrl}
                onChange={(event) => set({ iconUrl: event.target.value })}
                placeholder='اختر أيقونة من المعرض، أو الصق رابطها هنا'
              />
              <button
                type='button'
                className={styles['icon-browse']}
                onClick={() => setIconPickerOpen(true)}
              >
                {form.iconUrl ? 'تغيير الأيقونة' : 'اختيار من المعرض'}
              </button>
              {/* Only once there is something to clear. Emptying the text
                  input by hand works too, but an operator who picked the icon
                  from a gallery has no reason to expect that to be the way
                  back — and the field accepts a pasted URL, so it can hold a
                  long string that is tedious to select. */}
              {form.iconUrl && (
                <button
                  type='button'
                  className={styles['icon-clear']}
                  onClick={() => set({ iconUrl: '' })}
                >
                  إزالة الأيقونة
                </button>
              )}
            </div>
            {warnings.iconUrl && (
              <span className={styles.warning}>{warnings.iconUrl}</span>
            )}
          </div>

          <div className={styles.switches}>
            <label className={styles.switch}>
              <input
                type='checkbox'
                checked={form.openInNewTab}
                onChange={(event) => set({ openInNewTab: event.target.checked })}
              />
              <span>فتح في نافذة جديدة</span>
            </label>

            <label className={styles.switch}>
              <input
                type='checkbox'
                checked={form.isActive}
                onChange={(event) => set({ isActive: event.target.checked })}
              />
              <span>
                مفعّل
                <small>عند الإلغاء يختفي العنصر من كل القوائم</small>
              </span>
            </label>

            <label className={styles.switch}>
              <input
                type='checkbox'
                checked={form.isVisible}
                onChange={(event) => set({ isVisible: event.target.checked })}
              />
              <span>
                ظاهر في هذه القائمة
                <small>يؤثر على هذه القائمة فقط</small>
              </span>
            </label>

            {/* Offered on the NESTING menus only. A heading is a column title
                over the rows beneath it; in a flat menu there is nothing for it
                to head, and it would just be an unclickable row. */}
            {placementConfig?.supportsNesting && (
              <label className={styles.switch}>
                <input
                  type='checkbox'
                  checked={form.isHeadTitle}
                  onChange={(event) =>
                    set({
                      isHeadTitle: event.target.checked,
                      // A heading is not a button. Clearing both here means the
                      // operator never has to find and undo the contradiction
                      // the validator would otherwise report.
                      isDisplayTypeButton: event.target.checked
                        ? false
                        : form.isDisplayTypeButton,
                      isShowButtonText: event.target.checked
                        ? false
                        : form.isShowButtonText,
                    })
                  }
                />
                <span>
                  عنوان مجموعة
                  <small>يظهر كعنوان فوق العناصر التابعة، لا كرابط</small>
                </span>
              </label>
            )}

            {placementConfig?.supportsButtons && (
              <>
                <label className={styles.switch}>
                  <input
                    type='checkbox'
                    checked={form.isDisplayTypeButton}
                    onChange={(event) =>
                      set({
                        isDisplayTypeButton: event.target.checked,
                        isShowButtonText: event.target.checked
                          ? form.isShowButtonText
                          : false,
                      })
                    }
                  />
                  <span>يظهر كزر</span>
                </label>

                <label className={styles.switch}>
                  <input
                    type='checkbox'
                    checked={form.isShowButtonText}
                    disabled={!form.isDisplayTypeButton}
                    onChange={(event) => set({ isShowButtonText: event.target.checked })}
                  />
                  <span>إظهار نص الزر</span>
                </label>
              </>
            )}
          </div>

          {warnings.openInNewTab && (
            <p className={styles.warning}>{warnings.openInNewTab}</p>
          )}
          {showError('isShowButtonText') && (
            <p className={styles.error}>{errors.isShowButtonText}</p>
          )}
          {showError('isHeadTitle') && (
            <p className={styles.error}>{errors.isHeadTitle}</p>
          )}
        </fieldset>

        {/* ------------------------------------------------ copy to menus */}
        {/* One item can be placed in several menus — that is what the API's
            item/placement split is FOR. This is the only control that makes
            it reachable; without it an operator re-authors the entry and ends
            up with two items that drift apart on the next rename.

            MAIN MENU ONLY — see `isMainMenu` above for why. Not rendered at
            all rather than hidden, so `duplicateTargets` cannot be populated
            from a control the operator was never shown. */}
        {isMainMenu && (
        <fieldset
          className={styles.fieldset}
          hidden={currentStep.id !== STEP.COPY}
        >
          <legend className={styles.legend}>نسخ إلى قوائم أخرى</legend>

          <p className={styles.hint}>
            العنصر نفسه يظهر في القوائم المختارة — نسخة مشتركة، لا نسخة مستقلة.
            أي تعديل لاحق على العنوان أو الوجهة ينعكس على كل القوائم، بينما
            الإخفاء والحذف والترتيب تبقى لكل قائمة على حدة.
          </p>

          {isPlacementMapLoading ? (
            <p className={styles.hint}>جارٍ قراءة القوائم…</p>
          ) : (
            <div className={styles['duplicate-grid']}>
              {duplicateOptions.map((option) => {
                const isBlocked = option.presence !== 'available';
                const isChecked =
                  !isBlocked && duplicateTargets.includes(option.type);

                return (
                  <label
                    key={option.type}
                    className={[
                      styles['duplicate-option'],
                      isChecked ? styles['duplicate-checked'] : '',
                      styles[`duplicate-${option.presence}`],
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <input
                      type='checkbox'
                      checked={isChecked}
                      disabled={isBlocked}
                      onChange={() => toggleDuplicateTarget(option.type)}
                    />
                    <span>
                      <strong>{option.config.label}</strong>
                      <small>
                        {option.presence === 'present' && (
                          <span aria-hidden='true'>✓ </span>
                        )}
                        {option.reason}
                      </small>
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          {selectedTargets.length > 0 && (
            <p className={styles['duplicate-note']}>
              عند الحفظ سيُضاف هذا العنصر إلى {selectedTargets.length} قائمة
              أخرى، في نهاية كل منها.
            </p>
          )}
        </fieldset>
        )}

          </div>
        </div>

        <footer className={styles['dialog-foot']}>
          {/* «حفظ» is offered on EVERY step, not only the last.
              The later steps are optional — an entry with a title and a
              destination is already valid — so forcing a walk to the end
              before saving would be ceremony, not safety. «التالي» is the
              suggested path; «حفظ» is always available beside it. */}
          {!isLastStep && (
            <button
              type='button'
              className={styles['btn-primary']}
              onClick={handleNext}
              disabled={isSaving}
            >
              التالي
            </button>
          )}

          <button
            type='button'
            className={
              isLastStep ? styles['btn-primary'] : styles['btn-secondary']
            }
            onClick={handleSave}
            disabled={isSaving}
          >
            {/* The count is on the button because copying is a second,
                invisible write — «حفظ» alone would understate what is about
                to happen to four other menus. */}
            {isSaving
              ? 'جارٍ الحفظ…'
              : selectedTargets.length > 0
                ? `حفظ ونسخ إلى ${selectedTargets.length}`
                : 'حفظ'}
          </button>

          {stepIndex > 0 && (
            <button
              type='button'
              className={styles['btn-secondary']}
              onClick={handleBack}
              disabled={isSaving}
            >
              السابق
            </button>
          )}

          <button
            type='button'
            className={styles['btn-quiet']}
            onClick={onClose}
            disabled={isSaving}
          >
            إلغاء
          </button>

          <span className={styles['foot-progress']} aria-hidden='true'>
            {stepIndex + 1} / {steps.length}
          </span>

          {/* The validation notice lives NEXT TO the button it explains, so it
              is on screen at the exact moment حفظ appears to "do nothing". */}
          {touched && hasErrors && (
            <p className={styles['form-error']} role='alert'>
              {erroredSteps.has(currentStep.id)
                ? 'تحقق من الحقول المطلوبة في هذه الخطوة.'
                : 'هناك حقول ناقصة في خطوة سابقة — اضغط حفظ للانتقال إليها.'}
            </p>
          )}
        </footer>
      </div>

      {/* A second portal on top of this one. The Modal atom portals to
          <body>, so the later mount paints above — no z-index juggling. */}
      {isIconPickerOpen && (
        <IconPicker
          value={form.iconUrl}
          onSelect={(iconUrl) => set({ iconUrl })}
          onClose={() => setIconPickerOpen(false)}
          // Passed explicitly even though it is the picker's default: this is
          // the `ModuleKey` every icon uploaded from here is filed under, and
          // the name of the gallery the next editor will see. It belongs where
          // the picker is mounted, not buried in a default.
          moduleKey='NavigationMenu'
        />
      )}
    </Modal>
  );
};

export default MenuItemEditor;
